import { useAuth } from "@/contexts/AuthContext";
import { useMemo } from "react";

interface ApiClientOptions extends RequestInit {
  headers?: Record<string, string>;
}

export function useApiClient() {
  const { userData: session, getIdToken } = useAuth();

  const apiClient = useMemo(() => {
    const getHeaders = async (
      isFormData: boolean = false,
    ): Promise<Record<string, string>> => {
      const headers: Record<string, string> = {};

      // Only set Content-Type for JSON requests, not FormData
      if (!isFormData) {
        headers["Content-Type"] = "application/json";
      }

      // Add Firebase Authorization header
      try {
        const token = await getIdToken();
        
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        } else {
          console.warn(
            "[API Client] No token available - request will be unauthenticated",
          );
        }
      } catch (error) {
        console.error("[API Client] Failed to get ID token:", error);
      }

      if (session?.uid) {
        headers["x-user-id"] = session.uid;
      }

      if (session?.email) {
        headers["x-user-email"] = session.email;
      }

      if (session?.role) {
        headers["x-user-role"] = session.role;
      }

      // All authenticated users MUST have a tenantId
      if (session?.tenantId) {
        headers["x-tenant-id"] = session.tenantId;
      } else {
        console.error(
          "[API Client] User session missing tenantId - this should not happen!",
        );
        // Fallback to asepha to prevent API errors
        headers["x-tenant-id"] = "asepha";
      }

      return headers;
    };

    const apiFetch = async (
      url: string,
      options: ApiClientOptions = {},
    ): Promise<Response> => {
      // Check if the body is FormData
      const isFormData = options.body instanceof FormData;
      const defaultHeaders = await getHeaders(isFormData);

      const mergedOptions: RequestInit = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      };

      // Use the API base URL from environment variables
      const baseUrl = import.meta.env.VITE_API_URL
      const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;

      return fetch(fullUrl, mergedOptions);
    };

    return {
      fetch: apiFetch,
      get: (url: string, options: ApiClientOptions = {}) =>
        apiFetch(url, { ...options, method: "GET" }),
      post: (url: string, data?: any, options: ApiClientOptions = {}) => {
        const body = data ? JSON.stringify(data) : undefined;
        return apiFetch(url, { ...options, method: "POST", body });
      },
      put: (url: string, data?: any, options: ApiClientOptions = {}) => {
        const body = data ? JSON.stringify(data) : undefined;
        return apiFetch(url, { ...options, method: "PUT", body });
      },
      delete: (url: string, options: ApiClientOptions = {}) =>
        apiFetch(url, { ...options, method: "DELETE" }),
      patch: (url: string, data?: any, options: ApiClientOptions = {}) => {
        const body = data ? JSON.stringify(data) : undefined;
        return apiFetch(url, { ...options, method: "PATCH", body });
      },
    };
  }, [session, getIdToken]);

  return apiClient;
}
